/**
 * Parwaaz backend — the single dispatcher implementing every action in `packages/shared/src/api.ts`.
 *
 * `createApi` closes over Store/Platform/tenant/mail and returns one function, `(req) => res`,
 * the same shape served by Apps Script, the Node dev server and the in-browser demo. Mutating
 * actions run inside `platform.withLock`; reads do not. Every thrown error is mapped to an
 * `ApiErrorCode` here — handlers below throw plain, readable errors and never touch that mapping.
 */
import {
  APPLICATION_STATES,
  buildPayload,
  canonicalRevocationList,
  cardValidity,
  hoursBetween,
  isIndian,
  photoHash12,
  riskTier,
  runRules,
  seasonYear,
  todayIso,
  translitForCard,
  verifyPayload,
  type ActionName,
  type AdminBootstrapReq,
  type AdminBootstrapRes,
  type AdminConfigGetReq,
  type AdminConfigGetRes,
  type AdminConfigSetReq,
  type AdminExportReq,
  type AdminExportRes,
  type AdminInstallTriggersReq,
  type AdminInstallTriggersRes,
  type AdminProvisionStatusReq,
  type AdminProvisionStatusRes,
  type AdminRevocationDraftReq,
  type AdminRevocationDraftRes,
  type AdminRevokeListReq,
  type AdminSecretsSetReq,
  type AdminSecretsSetRes,
  type AdminStaffListReq,
  type AdminStaffListRes,
  type AdminStaffSetReq,
  type AllocateCardReq,
  type AllocateCardRes,
  type ApiErrorCode,
  type ApiRequest,
  type ApiResponse,
  type Application,
  type ApplicationState,
  type AttachCorrectionReq,
  type AttachCorrectionRes,
  type AttachSignatureReq,
  type AttachSignatureRes,
  type AuditRow,
  type CardRecord,
  type CaseActionReq,
  type CaseActionRes,
  type CaseReq,
  type CaseRes,
  type FileReq,
  type FileRes,
  type FileSlot,
  type HealthRes,
  type LoginReq,
  type LoginRes,
  type LogScansReq,
  type LogScansRes,
  type PilotFields,
  type PilotVisibleApplication,
  type PublicStatsRes,
  type QueueItem,
  type QueueReq,
  type QueueRes,
  type RevocationList,
  type RevocationsRes,
  type Role,
  type RiskTier,
  type SettableSecretName,
  type SetupReq,
  type SetupRes,
  type StatsReq,
  type StatsRes,
  type StatsSnapshot,
  type StatusReq,
  type StatusRes,
  type SubmitReq,
  type SubmitRes,
  type TenantConfig,
  type UploadFile,
} from '@parwaaz/shared';
import { BusyError, Card, ConflictError, NotFoundError, Platform, Store } from '../store';
import { constantTimeEqual, login as authLogin, requireRole, verifySession } from './auth';
import { ForbiddenError, IllegalTransitionError, RateLimitedError, UnauthorizedError, ValidationError } from './errors';
import type { Mailer } from './notify';
import { advanceAfterValidation, allowedActions, applyTransition } from './state';
import { approvedEmail, correctionRequestedEmail, receivedEmail, rejectedEmail } from './templates';

export interface CreateApiDeps {
  store: Store;
  platform: Platform;
  tenant: TenantConfig;
  mail: Mailer;
}

const MAX_FILES = 8;
const MAX_FILE_BYTES = 1.5 * 1024 * 1024;
const ALLOWED_MIME = new Set(['image/jpeg', 'image/png', 'application/pdf']);
const ALLOWED_SUBMIT_SLOTS: readonly FileSlot[] = ['doc', 'photo', 'cert', 'ins'];

const MUTATING_ACTIONS = new Set<ActionName>([
  'submit',
  'attachCorrection',
  'caseAction',
  'allocateCard',
  'attachSignature',
  'logScans',
  'admin.staffSet',
  'admin.configSet',
  'admin.publishRevocations',
  'setup',
  'admin.bootstrap',
  'admin.secretsSet',
  'admin.installTriggers',
]);

/** Secret names `admin.secretsSet`/`admin.provisionStatus` are allowed to touch — see
 *  `packages/shared/src/api.ts`'s `SettableSecretName`. An empty string value deletes the secret;
 *  values are never echoed back by either action. */
const SETTABLE_SECRETS: readonly SettableSecretName[] = ['ADMIN_EMAIL', 'MAIL_PROVIDER', 'MAIL_FROM_NAME', 'BREVO_API_KEY', 'GITHUB_REPO', 'GITHUB_EXPORT_TOKEN'];

// ── error mapping ────────────────────────────────────────────────────────────

function errorCode(err: unknown): ApiErrorCode {
  if (err instanceof ConflictError) return 'conflict';
  if (err instanceof BusyError) return 'busy';
  if (err instanceof NotFoundError) return 'not_found';
  if (err instanceof ForbiddenError) return 'forbidden';
  if (err instanceof UnauthorizedError) return 'unauthorized';
  if (err instanceof RateLimitedError) return 'rate_limited';
  if (err instanceof IllegalTransitionError) return 'illegal_transition';
  if (err instanceof ValidationError) return 'bad_request';
  return 'internal';
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'something went wrong';
}

// ── small shared helpers ─────────────────────────────────────────────────────

function validateSubmitFiles(files: UploadFile[]): void {
  if (files.length > MAX_FILES) throw new ValidationError(`too many files — at most ${MAX_FILES}`);
  for (const f of files) {
    if (!ALLOWED_SUBMIT_SLOTS.includes(f.slot)) throw new ValidationError(`'${f.slot}' is not a file slot a pilot can upload to`);
    if (!ALLOWED_MIME.has(f.mime)) throw new ValidationError(`'${f.name}' has an unsupported file type (${f.mime})`);
    const approxBytes = Math.floor((f.data.length * 3) / 4);
    if (approxBytes > MAX_FILE_BYTES) throw new ValidationError(`'${f.name}' is larger than 1.5 MB`);
  }
}

function matchesBlocklist(
  fields: PilotFields,
  entries: { docNumber?: string; name?: string; dob?: string; reason: string }[],
): boolean {
  const fullName = `${fields.givenNames} ${fields.surname}`.trim().toLowerCase();
  return entries.some((e) => {
    if (e.docNumber && e.docNumber === fields.docNumber) return true;
    return !!(e.name && e.dob && e.name.trim().toLowerCase() === fullName && e.dob === fields.dob);
  });
}

function blankPilotFields(): PilotFields {
  return {
    givenNames: '', surname: '', fathersName: '', addressLine: '', city: '', region: '', country: '', postcode: '',
    nationality: '', dob: '', phone: '', email: '',
    docType: 'other', docNumber: '', docIssuingCountry: '', docExpiry: '', mrzLine1: '', mrzLine2: '',
    isForeign: false, arrivalDate: '', visaType: '', visaValidFrom: '', visaValidTo: '',
    certBody: '', certLevel: '', certNumber: '', certIssued: '', certExpiry: '', certHolderName: '',
    gliderMake: '', gliderModel: '', gliderSize: '', gliderClass: 'unknown', reserveMake: '', reserveModel: '', reserveRepack: '',
    insProvider: '', insPolicyNo: '', insInsuredName: '', insCoverText: '', insValidTo: '', medicalValidTo: '',
    emgName: '', emgRelationship: '', emgPhone: '', bloodGroup: '',
    declarationAccepted: [], bondAccepted: [], typedName: '', consentPurpose: false,
  };
}

function maskDocNumber(doc: string): string {
  if (doc.length <= 4) return '*'.repeat(doc.length);
  return `${'*'.repeat(doc.length - 4)}${doc.slice(-4)}`;
}

function shapeForResponder(app: Application): Application {
  const fields = blankPilotFields();
  fields.givenNames = app.fields.givenNames;
  fields.surname = app.fields.surname;
  fields.nationality = app.fields.nationality;
  fields.phone = app.fields.phone;
  fields.emgName = app.fields.emgName;
  fields.emgRelationship = app.fields.emgRelationship;
  fields.emgPhone = app.fields.emgPhone;
  fields.bloodGroup = app.fields.bloodGroup;
  fields.medicalValidTo = app.fields.medicalValidTo;
  return { ...app, fields, mrz: null, findings: [], notes: [], files: {}, photoSha256: undefined, driveFolderId: undefined };
}

/** Reads are shaped by role here — never in the browser. See the brief for the exact per-role rules. */
function shapeApplicationForRole(app: Application, role: Role): Application {
  if (role === 'responder') return shapeForResponder(app);
  if (role === 'evaluator') {
    return { ...app, fields: { ...app.fields, docNumber: maskDocNumber(app.fields.docNumber), addressLine: '', city: '', region: '', postcode: '' } };
  }
  if (role === 'assistant' || role === 'dc') {
    return { ...app, fields: { ...app.fields, docNumber: maskDocNumber(app.fields.docNumber) } };
  }
  return app; // sdm, admin: unrestricted
}

function findFileSlot(store: Store, fileId: string): FileSlot | null {
  for (const app of store.listApplications()) {
    for (const [slot, id] of Object.entries(app.files)) {
      if (id === fileId) return slot as FileSlot;
    }
  }
  return null;
}

const TIMELINE_LABELS: Record<string, string> = {
  submit: 'Application submitted',
  validate: 'Validated',
  attachCorrection: 'Correction received',
  requestCorrection: 'Correction requested',
  evaluate: 'Evaluated',
  recommend: 'Sent for approval',
  approve: 'Approved',
  reject: 'Not approved',
  revoke: 'Card revoked',
  reissue: 'Card reissue approved',
  markPrinted: 'Card printed',
  override: 'Finding reviewed by an officer',
  reopen: 'Application reopened',
  assign: 'Assigned to an officer',
  note: 'Internal note added',
  allocateCard: 'Card number allocated',
  attachSignature: 'Card issued',
};

function pilotLabel(row: AuditRow): string {
  if (row.action === 'validate' && row.toState === 'Submitted') return 'Correction required';
  if (row.action === 'evaluate' && row.toState === 'EvaluationFailed') return 'Evaluation did not pass';
  return TIMELINE_LABELS[row.action] ?? row.action;
}

function csvEscape(value: unknown): string {
  const s = value === undefined || value === null ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function csvRow(values: unknown[]): string {
  return values.map(csvEscape).join(',');
}

function addDaysIso(iso: string, days: number): string {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function isoWeek(iso: string): string {
  const d = new Date(iso);
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function trySend(platform: Platform, fn: () => void): void {
  try {
    fn();
  } catch (err) {
    platform.log('mail send failed', err);
  }
}

// ── submission rate limiting ─────────────────────────────────────────────────
// Apps Script cannot see the caller's IP (there is no per-IP anything available to the
// handler), so `submit`/`attachCorrection` share one global sliding-window counter instead of a
// per-caller one — good enough to blunt a scripted flood without needing infrastructure this
// product doesn't have. Config keys `submit_window_minutes` / `submit_window_max` (both optional,
// defaulting below) size the window; `submit_window_json` holds the counter itself.

const SUBMIT_WINDOW_MINUTES_DEFAULT = 10;
const SUBMIT_WINDOW_MAX_DEFAULT = 60;

interface SubmitWindowState {
  start: string; // ISO timestamp the current window opened
  count: number;
}

function readSubmitWindowState(store: Store): SubmitWindowState | null {
  const raw = store.getConfig('submit_window_json');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as SubmitWindowState;
  } catch {
    return null;
  }
}

/** Throws RateLimitedError once `submit_window_max` submissions have landed inside the current
 *  `submit_window_minutes` window; otherwise records this call and lets the window roll over
 *  once `state.start` is older than the window. */
function assertSubmitWindowOk(store: Store, now: string): void {
  const windowMinutes = Number(store.getConfig('submit_window_minutes')) || SUBMIT_WINDOW_MINUTES_DEFAULT;
  const windowMax = Number(store.getConfig('submit_window_max')) || SUBMIT_WINDOW_MAX_DEFAULT;
  const state = readSubmitWindowState(store);

  if (!state || hoursBetween(state.start, now) * 60 >= windowMinutes) {
    store.setConfig('submit_window_json', JSON.stringify({ start: now, count: 1 } satisfies SubmitWindowState));
    return;
  }
  if (state.count >= windowMax) {
    throw new RateLimitedError('busy — please try again in a few minutes');
  }
  store.setConfig('submit_window_json', JSON.stringify({ start: state.start, count: state.count + 1 } satisfies SubmitWindowState));
}

// ── dashboard aggregate (also used by the nightly job) ──────────────────────

export function buildStats(store: Store, tenant: TenantConfig, now: string): StatsSnapshot {
  const apps = store.listApplications({ season: tenant.season.key });
  const byState = Object.fromEntries(APPLICATION_STATES.map((s) => [s, 0])) as Record<ApplicationState, number>;
  const byTier: Record<RiskTier, number> = { green: 0, amber: 0, red: 0 };
  const nationalityCounts = new Map<string, number>();
  const dayCounts = new Map<string, { submissions: number; issued: number }>();
  const evaluatorCounts = new Map<string, { count: number; pass: number; fail: number }>();
  const findingCounts = new Map<string, number>();
  let indian = 0, foreign = 0, cardsIssued = 0, cardsActive = 0, cardsRevoked = 0, expiringIn7 = 0;
  let approvalHoursTotal = 0, approvalCount = 0;

  for (const a of apps) {
    byState[a.status] += 1;
    byTier[a.riskTier] += 1;
    nationalityCounts.set(a.fields.nationality, (nationalityCounts.get(a.fields.nationality) ?? 0) + 1);
    if (isIndian(a.fields.nationality)) indian += 1;
    else foreign += 1;

    const day = a.createdAt.slice(0, 10);
    dayCounts.set(day, { ...(dayCounts.get(day) ?? { submissions: 0, issued: 0 }), submissions: (dayCounts.get(day)?.submissions ?? 0) + 1 });

    if (a.card?.issuedAt) {
      cardsIssued += 1;
      const issuedDay = a.card.issuedAt.slice(0, 10);
      dayCounts.set(issuedDay, { ...(dayCounts.get(issuedDay) ?? { submissions: 0, issued: 0 }), issued: (dayCounts.get(issuedDay)?.issued ?? 0) + 1 });
    }
    if (a.status === 'CardIssued') {
      cardsActive += 1;
      if (a.card && a.card.validTo <= addDaysIso(now, 7) && a.card.validTo >= now.slice(0, 10)) expiringIn7 += 1;
    }
    if (a.status === 'Revoked') cardsRevoked += 1;

    if (a.evaluatorUser) {
      const e = evaluatorCounts.get(a.evaluatorUser) ?? { count: 0, pass: 0, fail: 0 };
      e.count += 1;
      if (a.assessmentOutcome === 'fail') e.fail += 1;
      else if (a.assessmentOutcome) e.pass += 1;
      evaluatorCounts.set(a.evaluatorUser, e);
    }
    for (const f of a.findings) {
      if (f.severity === 'fail') findingCounts.set(f.rule, (findingCounts.get(f.rule) ?? 0) + 1);
    }
    if (a.decidedAt) {
      approvalHoursTotal += hoursBetween(a.createdAt, a.decidedAt);
      approvalCount += 1;
    }
  }

  const queueAges = APPLICATION_STATES.map((s) => {
    const inState = apps.filter((a) => a.status === s);
    const oldest = inState.reduce((min, a) => (a.updatedAt < min ? a.updatedAt : min), now);
    return { state: s, count: inState.length, oldestHours: inState.length ? hoursBetween(oldest, now) : 0 };
  });

  return {
    generatedAt: now,
    season: tenant.season.key,
    totals: { applications: apps.length, byState, cardsIssued, cardsActive, cardsRevoked, indian, foreign },
    byNationality: [...nationalityCounts.entries()].map(([nationality, count]) => ({ nationality, count })).sort((a, b) => b.count - a.count),
    byDay: [...dayCounts.entries()].map(([date, v]) => ({ date, ...v })).sort((a, b) => a.date.localeCompare(b.date)),
    byTier,
    byEvaluator: [...evaluatorCounts.entries()].map(([user, v]) => ({ user, ...v })),
    queueAges,
    expiringIn7Days: expiringIn7,
    avgApprovalHours: approvalCount ? approvalHoursTotal / approvalCount : null,
    topFindings: [...findingCounts.entries()].map(([rule, count]) => ({ rule, count })).sort((a, b) => b.count - a.count).slice(0, 10),
  };
}

// ── the dispatcher ────────────────────────────────────────────────────────────

export function createApi(deps: CreateApiDeps): (req: ApiRequest) => ApiResponse<any> {
  const { store, platform, tenant, mail } = deps;

  function requireSession(token: string) {
    const session = verifySession(store, platform, token);
    if (!session) throw new UnauthorizedError('sign in required');
    return session;
  }

  function health(): HealthRes {
    return { ok: true, tenantId: tenant.tenantId, rulesetVersion: tenant.rules.rulesetVersion, season: tenant.season.key, runtime: platform.runtime, now: platform.now() };
  }

  function submit(req: SubmitReq): SubmitRes {
    const existing = store.findByIdem(req.idemKey);
    if (existing) {
      return { ref: existing.ref, statusToken: '', findings: existing.findings, riskTier: existing.riskTier, status: existing.status, replay: true };
    }
    const now = platform.now();
    assertSubmitWindowOk(store, now);
    validateSubmitFiles(req.files);

    const ref = `A-${tenant.season.key}-${platform.uuid().slice(0, 8)}`;
    const files: Partial<Record<FileSlot, string>> = {};
    for (const f of req.files) files[f.slot] = store.putFile(ref, f.slot, f.name, f.mime, f.data);
    const photoFile = req.files.find((f) => f.slot === 'photo');
    const photoSha256 = photoFile ? platform.sha256(photoFile.data) : undefined;

    const duplicates = {
      activeCardForDoc: !!store.findActiveByDocNumber(req.fields.docNumber, tenant.season.key),
      onBlocklist: matchesBlocklist(req.fields, store.listBlocklist()),
    };
    const findings = runRules(req.fields, req.mrz, { today: todayIso(), tenant, duplicates });
    const tier = riskTier(findings);
    const statusToken = platform.randomToken(24);
    const advance = advanceAfterValidation(tier, findings, now);

    const app: Application = {
      ref, status: advance.status, season: tenant.season.key, createdAt: now, updatedAt: now,
      idemKey: req.idemKey, statusTokenHash: platform.sha256B64url(statusToken),
      fields: req.fields, mrz: req.mrz, findings, riskTier: tier, rulesetVersion: tenant.rules.rulesetVersion,
      correctionRequest: advance.correctionRequest, notes: [], card: null, files, photoSha256,
    };
    store.appendApplication(app);

    store.appendAudit({ at: now, actor: 'pilot', role: 'pilot', action: 'submit', entity: 'application', entityId: ref, fromState: '', toState: 'Submitted', reason: 'application submitted' });
    if (advance.status !== 'Submitted' || tier === 'red') {
      store.appendAudit({
        at: now, actor: 'system', role: 'system', action: 'validate', entity: 'application', entityId: ref,
        fromState: 'Submitted', toState: advance.status,
        reason: tier === 'red' ? 'red tier: correction required' : 'validated, queued for evaluation',
      });
    }

    if (req.fields.email) {
      trySend(platform, () => {
        const statusUrl = `${tenant.product.publicUrl}/status/${ref}?t=${statusToken}`;
        const content = receivedEmail(tenant, ref, statusUrl);
        mail.send(req.fields.email, content.subject, content.html, content.text);
      });
    }

    return { ref, statusToken, findings, riskTier: tier, status: app.status };
  }

  function status(req: StatusReq): StatusRes {
    const app = store.getApplication(req.ref);
    if (!app || !constantTimeEqual(platform.sha256B64url(req.token), app.statusTokenHash)) throw new UnauthorizedError('invalid status link');

    const timeline = store.listAudit(app.ref).map((row) => ({ at: row.at, label: pilotLabel(row), state: row.toState || row.fromState, detail: row.reason }));
    let photoDataUrl: string | undefined;
    if (app.card && app.files.photo) {
      const f = store.getFile(app.files.photo);
      if (f) photoDataUrl = `data:${f.mime};base64,${f.data}`;
    }

    const application: PilotVisibleApplication = {
      ref: app.ref, status: app.status, createdAt: app.createdAt, updatedAt: app.updatedAt,
      fields: {
        givenNames: app.fields.givenNames, surname: app.fields.surname, nationality: app.fields.nationality,
        certBody: app.fields.certBody, certLevel: app.fields.certLevel, gliderMake: app.fields.gliderMake,
        gliderModel: app.fields.gliderModel, email: app.fields.email, phone: app.fields.phone,
      },
      findings: app.findings, riskTier: app.riskTier, correctionRequest: app.correctionRequest,
      card: app.card ? { cardNo: app.card.cardNo, validFrom: app.card.validFrom, validTo: app.card.validTo, payload: app.card.payload, signature: app.card.signature, keyId: app.card.keyId, revokedAt: app.card.revokedAt } : null,
      photoDataUrl,
    };
    return { application, timeline };
  }

  function attachCorrection(req: AttachCorrectionReq): AttachCorrectionRes {
    const app = store.getApplication(req.ref);
    if (!app || !constantTimeEqual(platform.sha256B64url(req.token), app.statusTokenHash)) throw new UnauthorizedError('invalid status link');
    // Reachable either via an officer's explicit requestCorrection (status CorrectionRequested) or
    // the automatic red-tier outcome of submit/attachCorrection itself (status stays Submitted).
    if (!app.correctionRequest) throw new IllegalTransitionError('this application is not awaiting a correction');

    const now = platform.now();
    assertSubmitWindowOk(store, now);
    const fields: PilotFields = { ...app.fields, ...(req.fields ?? {}) };
    const files = { ...app.files };
    if (req.files && req.files.length) {
      validateSubmitFiles(req.files);
      for (const f of req.files) files[f.slot] = store.putFile(app.ref, f.slot, f.name, f.mime, f.data);
    }
    const photoFile = req.files?.find((f) => f.slot === 'photo');
    const photoSha256 = photoFile ? platform.sha256(photoFile.data) : app.photoSha256;

    // Exclude the application being corrected itself — it's already in the store under this doc number.
    const activeDup = store.findActiveByDocNumber(fields.docNumber, app.season);
    const duplicates = { activeCardForDoc: !!activeDup && activeDup.ref !== app.ref, onBlocklist: matchesBlocklist(fields, store.listBlocklist()) };
    const findings = runRules(fields, app.mrz, { today: todayIso(), tenant, duplicates });
    const tier = riskTier(findings);
    const advance = advanceAfterValidation(tier, findings, now);

    const updated = store.updateApplication(app.ref, { fields, files, photoSha256, findings, riskTier: tier, status: advance.status, correctionRequest: advance.correctionRequest });
    store.appendAudit({ at: now, actor: 'pilot', role: 'pilot', action: 'attachCorrection', entity: 'application', entityId: app.ref, fromState: app.status, toState: advance.status, reason: req.message ?? 'correction submitted' });

    return { status: updated.status, findings, riskTier: tier };
  }

  function doLogin(req: LoginReq): LoginRes {
    return authLogin(store, platform, req.user, req.derivedKey);
  }

  const QUEUE_ROLES: readonly Role[] = ['evaluator', 'assistant', 'sdm', 'dc', 'admin'];
  const EVALUATOR_QUEUE_STATES: readonly ApplicationState[] = ['ReadyForEvaluation', 'Evaluated', 'EvaluationFailed'];

  function queue(req: QueueReq): QueueRes {
    const session = requireSession(req.session);
    if (!QUEUE_ROLES.includes(session.role)) throw new ForbiddenError(`role '${session.role}' has no queue view`);

    let states = req.filter?.states;
    if (session.role === 'evaluator') states = states ? states.filter((s) => EVALUATOR_QUEUE_STATES.includes(s)) : [...EVALUATOR_QUEUE_STATES];

    let apps = store.listApplications({ season: tenant.season.key, states, search: req.filter?.search, limit: req.filter?.limit });
    if (req.filter?.tier) apps = apps.filter((a) => a.riskTier === req.filter!.tier);
    if (req.filter?.nationality === 'indian') apps = apps.filter((a) => isIndian(a.fields.nationality));
    else if (req.filter?.nationality === 'foreign') apps = apps.filter((a) => !isIndian(a.fields.nationality));
    else if (req.filter?.nationality) apps = apps.filter((a) => a.fields.nationality === req.filter!.nationality);
    if (req.filter?.assignedTo) apps = apps.filter((a) => a.assignedTo === req.filter!.assignedTo);

    const now = platform.now();
    const items: QueueItem[] = apps.map((a) => ({
      ref: a.ref, status: a.status, createdAt: a.createdAt, updatedAt: a.updatedAt,
      name: `${a.fields.givenNames} ${a.fields.surname}`.trim(), nationality: a.fields.nationality, isForeign: !isIndian(a.fields.nationality),
      certLevel: a.fields.certLevel, gliderClass: a.fields.gliderClass, riskTier: a.riskTier,
      failCount: a.findings.filter((f) => f.severity === 'fail' && !f.overridden).length,
      warnCount: a.findings.filter((f) => f.severity === 'warn').length,
      assignedTo: a.assignedTo, cardNo: a.card?.cardNo, ageHours: hoursBetween(a.createdAt, now),
    }));

    const counts: Record<string, number> = {};
    for (const a of store.listApplications({ season: tenant.season.key })) counts[a.status] = (counts[a.status] ?? 0) + 1;
    return { items, counts };
  }

  function caseView(req: CaseReq): CaseRes {
    const session = requireSession(req.session);
    if (session.role === 'pilot' || session.role === 'verifier') throw new ForbiddenError(`role '${session.role}' cannot open a case`);
    const app = store.getApplication(req.ref);
    if (!app) throw new NotFoundError(`no such application '${req.ref}'`);
    return { application: shapeApplicationForRole(app, session.role), audit: store.listAudit(app.ref), allowedActions: allowedActions(app, session.role) };
  }

  function fileView(req: FileReq): FileRes {
    const session = requireSession(req.session);
    if (session.role === 'responder' || session.role === 'pilot' || session.role === 'verifier') throw new ForbiddenError(`role '${session.role}' cannot read files`);
    const file = store.getFile(req.fileId);
    if (!file) throw new NotFoundError(`no such file '${req.fileId}'`);
    if (session.role === 'evaluator') {
      const slot = findFileSlot(store, req.fileId);
      if (slot !== 'cert' && slot !== 'photo') throw new ForbiddenError('evaluators may only read certificate and photo files');
    }
    return { mime: file.mime, data: file.data, name: file.name };
  }

  function caseAction(req: CaseActionReq): CaseActionRes {
    const session = requireSession(req.session);
    const app = store.getApplication(req.ref);
    if (!app) throw new NotFoundError(`no such application '${req.ref}'`);

    const now = platform.now();
    const result = applyTransition(app, req.name, session.role, session.user, req.payload ?? {}, req.reason, now);
    const updated = store.updateApplication(app.ref, result.patch, req.expectedUpdatedAt);
    store.appendAudit({ at: now, actor: session.user, role: session.role, action: req.name, entity: 'application', entityId: app.ref, fromState: result.fromState, toState: result.toState, reason: result.reason });

    if (req.name === 'reissue' && app.card) {
      store.upsertCard({ ...app.card, ref: app.ref, revokedAt: app.card.revokedAt ?? now, revocationReason: app.card.revocationReason ?? 'reissued' });
    }
    // revoke/markPrinted patch application.card only — keep the store's separate `cards` list in sync too.
    if ((req.name === 'revoke' || req.name === 'markPrinted') && updated.card) {
      store.upsertCard({ ...updated.card, ref: updated.ref });
    }
    if (app.fields.email) {
      if (req.name === 'requestCorrection') trySend(platform, () => { const c = correctionRequestedEmail(tenant, app.ref, req.payload?.correctionItems ?? []); mail.send(app.fields.email, c.subject, c.html, c.text); });
      if (req.name === 'approve') trySend(platform, () => { const c = approvedEmail(tenant, app.ref); mail.send(app.fields.email, c.subject, c.html, c.text); });
      if (req.name === 'reject') trySend(platform, () => { const c = rejectedEmail(tenant, app.ref, req.reason ?? 'not specified'); mail.send(app.fields.email, c.subject, c.html, c.text); });
    }

    return { application: updated, allowedActions: allowedActions(updated, session.role) };
  }

  function allocateCard(req: AllocateCardReq): AllocateCardRes {
    const session = requireSession(req.session);
    requireRole(session, ['sdm', 'admin']);
    const app = store.getApplication(req.ref);
    if (!app) throw new NotFoundError(`no such application '${req.ref}'`);
    if (app.status !== 'Approved') throw new IllegalTransitionError('a card can only be allocated for an approved application');
    if (!app.photoSha256) throw new ValidationError('no photo on file for this application');

    const cardNumber = store.allocateCardNo(tenant.season.cardPrefix, seasonYear(tenant));
    const validity = cardValidity(tenant, todayIso());
    const keyId = store.getConfig('key_id') ?? tenant.keys[tenant.keys.length - 1]!.kid;
    const name = translitForCard(app.fields.surname, app.fields.givenNames);
    const hash12 = photoHash12(app.photoSha256);
    const payload = buildPayload({ cardNo: cardNumber, name, nationality: app.fields.nationality, validFrom: validity.validFrom, validTo: validity.validTo, cert: app.fields.certLevel, photoHash12: hash12, keyId });

    const pendingCard: CardRecord = { cardNo: cardNumber, validFrom: validity.validFrom, validTo: validity.validTo, keyId, payload, signature: '', photoHash12: hash12, issuedAt: '', reissuedFrom: app.card?.cardNo };
    const updated = store.updateApplication(app.ref, { card: pendingCard }, req.expectedUpdatedAt);
    store.appendAudit({ at: updated.updatedAt, actor: session.user, role: session.role, action: 'allocateCard', entity: 'application', entityId: app.ref, fromState: 'Approved', toState: 'Approved', reason: `card ${cardNumber} allocated` });

    return { cardNo: cardNumber, validFrom: validity.validFrom, validTo: validity.validTo, payload, photoHash12: hash12, keyId, updatedAt: updated.updatedAt };
  }

  function attachSignature(req: AttachSignatureReq): AttachSignatureRes {
    const session = requireSession(req.session);
    requireRole(session, ['sdm', 'admin']);
    const app = store.getApplication(req.ref);
    if (!app) throw new NotFoundError(`no such application '${req.ref}'`);
    if (!app.card || app.card.cardNo !== req.cardNo || app.card.payload !== req.payload) throw new ValidationError('signature does not match the pending card allocation');
    const key = tenant.keys.find((k) => k.kid === req.keyId);
    if (!key) throw new ValidationError(`unknown signing key '${req.keyId}'`);
    if (!verifyPayload(req.payload, req.signature, key.publicKey)) throw new ValidationError('signature verification failed');

    const now = platform.now();
    const card: CardRecord = { ...app.card, signature: req.signature, issuedAt: now, keyId: req.keyId };
    const updated = store.updateApplication(app.ref, { status: 'CardIssued', card }, req.expectedUpdatedAt);
    store.upsertCard({ ...card, ref: app.ref });
    store.appendAudit({ at: now, actor: session.user, role: session.role, action: 'attachSignature', entity: 'application', entityId: app.ref, fromState: 'Approved', toState: 'CardIssued', reason: `card ${card.cardNo} issued` });

    return { application: updated };
  }

  function logScans(req: LogScansReq): LogScansRes {
    if (!req.deviceId) throw new ValidationError('deviceId is required');
    store.appendScans(req.scans);
    return { accepted: req.scans.length };
  }

  function revocations(): RevocationsRes {
    const raw = store.getConfig('revocations_json');
    if (raw) return JSON.parse(raw) as RevocationList;
    return { version: 0, generatedAt: platform.now(), keyId: tenant.keys[tenant.keys.length - 1]?.kid ?? '', revoked: [], signature: '' };
  }

  function stats(req: StatsReq): StatsRes {
    const session = requireSession(req.session);
    requireRole(session, ['sdm', 'dc', 'admin', 'assistant']);
    return buildStats(store, tenant, platform.now());
  }

  function publicStats(): PublicStatsRes {
    const apps = store.listApplications({ season: tenant.season.key });
    const nationalityCounts = new Map<string, number>();
    const weekCounts = new Map<string, number>();
    let cardsActive = 0;
    for (const a of apps) {
      nationalityCounts.set(a.fields.nationality, (nationalityCounts.get(a.fields.nationality) ?? 0) + 1);
      if (a.status === 'CardIssued') cardsActive += 1;
      const week = isoWeek(a.createdAt);
      weekCounts.set(week, (weekCounts.get(week) ?? 0) + 1);
    }
    return {
      generatedAt: platform.now(), season: tenant.season.key, seasonLabel: tenant.season.label,
      pilotsRegistered: apps.length, cardsActive, countries: nationalityCounts.size,
      topNationalities: [...nationalityCounts.entries()].map(([nationality, count]) => ({ nationality, count })).sort((a, b) => b.count - a.count).slice(0, 10),
      byWeek: [...weekCounts.entries()].map(([week, count]) => ({ week, count })).sort((a, b) => a.week.localeCompare(b.week)),
    };
  }

  function adminStaffList(req: AdminStaffListReq): AdminStaffListRes {
    requireRole(requireSession(req.session), ['admin']);
    return { staff: store.listStaff() };
  }

  function adminStaffSet(req: AdminStaffSetReq) {
    const session = requireSession(req.session);
    requireRole(session, ['admin']);
    store.upsertStaff(req.staff);
    if (req.derivedKey) store.setStaffSecret(req.staff.user, platform.sha256B64url(req.derivedKey));
    store.appendAudit({ at: platform.now(), actor: session.user, role: session.role, action: 'staffSet', entity: 'staff', entityId: req.staff.user, fromState: '', toState: '', reason: `staff record updated${req.derivedKey ? ' (passphrase reset)' : ''}` });
    return { staff: req.staff };
  }

  function adminConfigGet(req: AdminConfigGetReq): AdminConfigGetRes {
    requireRole(requireSession(req.session), ['admin']);
    // Rate-limit counters are operational noise, not configuration; keep them out of the admin view.
    const config = Object.fromEntries(Object.entries(store.allConfig()).filter(([k]) => !k.startsWith('login_fail_')));
    return { config };
  }

  function adminConfigSet(req: AdminConfigSetReq) {
    const session = requireSession(req.session);
    requireRole(session, ['admin']);
    store.setConfig(req.key, req.value);
    store.appendAudit({ at: platform.now(), actor: session.user, role: session.role, action: 'configSet', entity: 'config', entityId: req.key, fromState: '', toState: '', reason: `${req.key} = ${req.value}` });
    return { key: req.key, value: req.value };
  }

  function adminRevocationDraft(req: AdminRevocationDraftReq): AdminRevocationDraftRes {
    requireRole(requireSession(req.session), ['admin']);
    const revoked = store.listCards().filter((c) => c.revokedAt).map((c) => ({ cardNo: c.cardNo, at: c.revokedAt!, reason: c.revocationReason }));
    const version = Number(store.getConfig('revocation_version') ?? '0') + 1;
    const generatedAt = platform.now();
    const keyId = store.getConfig('key_id') ?? tenant.keys[tenant.keys.length - 1]!.kid;
    const canonical = canonicalRevocationList({ version, generatedAt, keyId, revoked });
    return { version, generatedAt, keyId, revoked, canonical };
  }

  function adminPublishRevocations(req: AdminRevokeListReq): RevocationList {
    const session = requireSession(req.session);
    requireRole(session, ['admin']);
    const key = tenant.keys.find((k) => k.kid === req.keyId);
    if (!key) throw new ValidationError(`unknown signing key '${req.keyId}'`);
    const revoked = store.listCards().filter((c) => c.revokedAt).map((c) => ({ cardNo: c.cardNo, at: c.revokedAt!, reason: c.revocationReason }));
    const canonical = canonicalRevocationList({ version: req.version, generatedAt: req.generatedAt, keyId: req.keyId, revoked });
    if (!verifyPayload(canonical, req.signature, key.publicKey)) throw new ValidationError('revocation list signature verification failed');

    const list: RevocationList = { version: req.version, generatedAt: req.generatedAt, keyId: req.keyId, revoked, signature: req.signature };
    store.setConfig('revocations_json', JSON.stringify(list));
    store.setConfig('revocation_version', String(req.version));
    store.appendAudit({ at: platform.now(), actor: session.user, role: session.role, action: 'publishRevocations', entity: 'config', entityId: 'revocations_json', fromState: '', toState: '', reason: `published revocation list v${req.version} (${revoked.length} cards)` });
    return list;
  }

  function adminExport(req: AdminExportReq): AdminExportRes {
    const session = requireSession(req.session);
    requireRole(session, ['admin']);
    const apps = store.listApplications({ season: tenant.season.key });
    const audit = store.listAudit();
    const cards = store.listCards();

    const appHeader = ['ref', 'status', 'season', 'createdAt', 'updatedAt', 'givenNames', 'surname', 'fathersName', 'nationality', 'dob', 'phone', 'email', 'docType', 'docNumber', 'docIssuingCountry', 'certBody', 'certLevel', 'certNumber', 'gliderMake', 'gliderModel', 'gliderClass', 'insProvider', 'insPolicyNo', 'insValidTo', 'riskTier', 'rulesetVersion', 'assignedTo', 'evaluatorUser', 'assessmentOutcome', 'reviewerUser', 'recommendation', 'approverUser', 'decision', 'decisionReason', 'cardNo', 'cardValidFrom', 'cardValidTo', 'cardIssuedAt', 'cardRevokedAt'];
    const appRows = apps.map((a) => csvRow([a.ref, a.status, a.season, a.createdAt, a.updatedAt, a.fields.givenNames, a.fields.surname, a.fields.fathersName, a.fields.nationality, a.fields.dob, a.fields.phone, a.fields.email, a.fields.docType, a.fields.docNumber, a.fields.docIssuingCountry, a.fields.certBody, a.fields.certLevel, a.fields.certNumber, a.fields.gliderMake, a.fields.gliderModel, a.fields.gliderClass, a.fields.insProvider, a.fields.insPolicyNo, a.fields.insValidTo, a.riskTier, a.rulesetVersion, a.assignedTo ?? '', a.evaluatorUser ?? '', a.assessmentOutcome ?? '', a.reviewerUser ?? '', a.recommendation ?? '', a.approverUser ?? '', a.decision ?? '', a.decisionReason ?? '', a.card?.cardNo ?? '', a.card?.validFrom ?? '', a.card?.validTo ?? '', a.card?.issuedAt ?? '', a.card?.revokedAt ?? '']));
    const applicationsCsv = [csvRow(appHeader), ...appRows].join('\r\n');

    const auditHeader = ['seq', 'at', 'actor', 'role', 'action', 'entity', 'entityId', 'fromState', 'toState', 'reason'];
    const auditRows = audit.map((r) => csvRow([r.seq, r.at, r.actor, r.role, r.action, r.entity, r.entityId, r.fromState, r.toState, r.reason]));
    const auditCsv = [csvRow(auditHeader), ...auditRows].join('\r\n');

    const cardsHeader = ['cardNo', 'ref', 'validFrom', 'validTo', 'keyId', 'issuedAt', 'printedAt', 'revokedAt', 'revocationReason', 'reissuedFrom'];
    const cardsRows: Card[] = cards;
    const cardsCsv = [csvRow(cardsHeader), ...cardsRows.map((c) => csvRow([c.cardNo, c.ref, c.validFrom, c.validTo, c.keyId, c.issuedAt, c.printedAt ?? '', c.revokedAt ?? '', c.revocationReason ?? '', c.reissuedFrom ?? '']))].join('\r\n');

    store.appendAudit({ at: platform.now(), actor: session.user, role: session.role, action: 'export', entity: 'system', entityId: 'export', fromState: '', toState: '', reason: `exported ${apps.length} applications` });
    return { applicationsCsv, auditCsv, cardsCsv, json: JSON.stringify({ applications: apps, audit, cards }, null, 2) };
  }

  // ── provisioning over HTTPS ─────────────────────────────────────────────────
  // See `packages/shared/src/api.ts`'s "Provisioning over HTTPS" section for the trust model:
  // `setup` and `admin.bootstrap` are accepted only while the backend is uninitialised, with no
  // secret presented, because the `/exec` URL is not shared with anyone until provisioning has
  // finished. `docs/SECURITY.md` → "First-run trust window" documents the same for the reader.

  function setup(req: SetupReq): SetupRes {
    const prov = platform.provisioning;
    if (!prov) {
      // Memory runtime (dev server / in-browser demo): no workbook to create — a fixed, harmless
      // reply. The real "has this backend been set up" gate for this runtime is admin.bootstrap's
      // own `listStaff().length === 0` check, not this action.
      return { sheetId: 'memory', driveFolderId: 'memory', created: false, tabs: [] };
    }
    if (!prov.isInitialised()) return prov.setup();
    // Already initialised: a repeat call is an authenticated, idempotent re-read of the ids, not
    // a first-run action — no session at all (missing or wrong role) is 'forbidden', matching
    // every other admin.* action's shape rather than 'unauthorized' (there is nothing to log in
    // *to* on a system that isn't newly provisioning).
    const session = req.session ? verifySession(store, platform, req.session) : null;
    if (!session || session.role !== 'admin') throw new ForbiddenError('the workbook is already set up — sign in as an admin to re-read its ids');
    return prov.setup();
  }

  function adminBootstrap(req: AdminBootstrapReq): AdminBootstrapRes {
    if (store.listStaff().length > 0) {
      throw new ForbiddenError('an admin already exists — sign in and use admin.staffSet to add more staff');
    }
    if (!req.user || !req.derivedKey || !req.displayName) throw new ValidationError('user, derivedKey and displayName are required');
    store.upsertStaff({ user: req.user, displayName: req.displayName, role: 'admin', active: true, notes: 'created by admin.bootstrap' });
    store.setStaffSecret(req.user, platform.sha256B64url(req.derivedKey));
    store.appendAudit({ at: platform.now(), actor: req.user, role: 'admin', action: 'bootstrap', entity: 'staff', entityId: req.user, fromState: '', toState: '', reason: 'first admin created via admin.bootstrap' });
    return authLogin(store, platform, req.user, req.derivedKey);
  }

  function adminSecretsSet(req: AdminSecretsSetReq): AdminSecretsSetRes {
    const session = requireSession(req.session);
    requireRole(session, ['admin']);
    const set: string[] = [];
    for (const [name, value] of Object.entries(req.secrets ?? {})) {
      if (!SETTABLE_SECRETS.includes(name as SettableSecretName)) throw new ValidationError(`'${name}' is not a settable secret`);
      if (value === '') store.deleteSecret(name);
      else if (value !== undefined) store.setSecret(name, value);
      set.push(name);
    }
    store.appendAudit({ at: platform.now(), actor: session.user, role: session.role, action: 'secretsSet', entity: 'config', entityId: 'secrets', fromState: '', toState: '', reason: `secrets updated: ${set.join(', ') || '(none)'}` });
    return { set };
  }

  function adminInstallTriggers(req: AdminInstallTriggersReq): AdminInstallTriggersRes {
    const session = requireSession(req.session);
    requireRole(session, ['admin']);
    const installed = platform.provisioning ? platform.provisioning.installTriggers() : [];
    store.appendAudit({
      at: platform.now(), actor: session.user, role: session.role, action: 'installTriggers', entity: 'system', entityId: 'triggers',
      fromState: '', toState: '', reason: installed.length ? `installed: ${installed.join(', ')}` : 'no triggers on this runtime',
    });
    return { installed };
  }

  function adminProvisionStatus(req: AdminProvisionStatusReq): AdminProvisionStatusRes {
    const prov = platform.provisioning;
    const staffCount = store.listStaff().length;
    // appsscript: "initialised" means the workbook exists. Memory runtime has no workbook — the
    // closest equivalent is "has at least one admin", i.e. admin.bootstrap has already run.
    const initialised = prov ? prov.isInitialised() : staffCount > 0;
    const session = req.session ? verifySession(store, platform, req.session) : null;
    if (!session) return { initialised, staffCount, triggers: [], secretsPresent: [], runtime: platform.runtime };
    requireRole(session, ['admin']);
    const triggers = prov ? prov.listTriggers() : [];
    const secretsPresent = SETTABLE_SECRETS.filter((name) => store.getSecret(name) !== null);
    const sheetId = prov ? prov.setup().sheetId : undefined;
    return { initialised, staffCount, triggers, secretsPresent, runtime: platform.runtime, sheetId };
  }

  function dispatch(req: ApiRequest): unknown {
    switch (req.action) {
      case 'health': return health();
      case 'submit': return submit(req);
      case 'status': return status(req);
      case 'attachCorrection': return attachCorrection(req);
      case 'login': return doLogin(req);
      case 'queue': return queue(req);
      case 'case': return caseView(req);
      case 'file': return fileView(req);
      case 'caseAction': return caseAction(req);
      case 'allocateCard': return allocateCard(req);
      case 'attachSignature': return attachSignature(req);
      case 'logScans': return logScans(req);
      case 'revocations': return revocations();
      case 'stats': return stats(req);
      case 'publicStats': return publicStats();
      case 'admin.staffList': return adminStaffList(req);
      case 'admin.staffSet': return adminStaffSet(req);
      case 'admin.configGet': return adminConfigGet(req);
      case 'admin.configSet': return adminConfigSet(req);
      case 'admin.publishRevocations': return adminPublishRevocations(req);
      case 'admin.revocationDraft': return adminRevocationDraft(req);
      case 'admin.export': return adminExport(req);
      case 'setup': return setup(req);
      case 'admin.bootstrap': return adminBootstrap(req);
      case 'admin.secretsSet': return adminSecretsSet(req);
      case 'admin.installTriggers': return adminInstallTriggers(req);
      case 'admin.provisionStatus': return adminProvisionStatus(req);
      default:
        throw new ValidationError(`unknown action '${(req as { action: string }).action}'`);
    }
  }

  return (req: ApiRequest): ApiResponse<any> => {
    const start = Date.now();
    try {
      const data = MUTATING_ACTIONS.has(req.action) ? platform.withLock(() => dispatch(req)) : dispatch(req);
      platform.log(req.action, `${Date.now() - start}ms`, 'ok');
      return { ok: true, data };
    } catch (err) {
      const code = errorCode(err);
      if (code === 'internal') platform.log(req.action, `${Date.now() - start}ms`, 'INTERNAL ERROR', err);
      else platform.log(req.action, `${Date.now() - start}ms`, code, errorMessage(err));
      return { ok: false, error: errorMessage(err), code };
    }
  };
}
